import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import {
  type AkiParamsItem,
  type AkiVasopressorsDTO,
  type BatchScoreRequestItemDto,
  type CaseWithPredictionResultDTO,
  type ClinicalScoreResultDto,
  type OrganSupportDTO,
  type PluginUdiDto,
  type VitalSignItemDTO
} from '@/generated'
import { AkiPredictionsService } from '@/services/telehealthApi/AkiPredictionsService'
import { EdsService } from '@/services/telehealthApi/EdsService'
import { OrganSupportService } from '@/services/telehealthApi/OrganSupportService'
import { VitalSignsService } from '@/services/telehealthApi/VitalSignsService'
import { NoticeType } from '@/stores/models/Notice'
import { useNoticeStore } from '@/stores/notice'
import { useEdsPlugin } from '@/composables/useEdsPlugin'
import { usePluginsStore } from '@/stores/plugins'
import { withMinimumDuration } from '@/util/withMinimumDuration'
import { formatVitalSigns } from '@/util/formatVitalSigns'
import i18n from '@/i18n/i18n'

const MINIMUM_LOADING_DURATION_MS = 300

export interface AkiCaseWithPredictionResult extends CaseWithPredictionResultDTO {
  edsScore?: ClinicalScoreResultDto
  organSupport?: OrganSupportDTO
  vitalSigns?: VitalSignItemDTO[]
  egfr?: AkiParamsItem[]
  temperature?: AkiParamsItem[]
  vasopressors?: AkiVasopressorsDTO
  trend?: string | null
  panelLoaded?: boolean
}

export const useAkiPredictionsStore = defineStore('akiPredictions', () => {
  const noticeStore = useNoticeStore()
  const { hasAcceptedEdsLimitation } = useEdsPlugin()
  const { isEdsEnabled } = storeToRefs(usePluginsStore())
  const isAkiLoading = ref(false)
  const isAkiCardDetailsLoading = ref(false)

  const udiData = ref<PluginUdiDto | null>(null)

  const totalAkiPatients = ref<AkiCaseWithPredictionResult[]>([])
  const totalAkiPatientsCount = ref<number | null>(null)
  const veryHighRiskAkiPatientsCount = ref<number | null>(null)
  const highRiskAkiPatientsCount = ref<number | null>(null)

  const currentPage = ref(1)
  const itemsPerPage = ref(10)
  const sortBy = ref('userName')
  const sortDirection = ref<'ASC' | 'DESC'>('ASC')

  async function getAkiPredictionsForUser(page = currentPage.value, size = itemsPerPage.value) {
    try {
      isAkiLoading.value = true

      const response = await withMinimumDuration(
        AkiPredictionsService.getAkiOverview({
          page: page - 1,
          size,
          sort: [`${sortBy.value},${sortDirection.value}`]
        }),
        MINIMUM_LOADING_DURATION_MS
      )

      currentPage.value = page

      totalAkiPatientsCount.value = response.totalAkiRiskPatients
      veryHighRiskAkiPatientsCount.value = response.veryHighRiskCount
      highRiskAkiPatientsCount.value = response.highRiskCount
      totalAkiPatients.value = response.results.filter(
        (result) =>
          result.predictionResult.probability === 'HIGH' ||
          result.predictionResult.probability === 'VERY_HIGH'
      )

      if (
        totalAkiPatients.value.length > 0 &&
        isEdsEnabled.value &&
        hasAcceptedEdsLimitation.value
      ) {
        await getEdsScoresForAkiPatients()
      }
    } catch {
      totalAkiPatients.value = []
      totalAkiPatientsCount.value = null
      veryHighRiskAkiPatientsCount.value = null
      highRiskAkiPatientsCount.value = null
      noticeStore.add({
        type: NoticeType.ERROR,
        message: i18n.global.t('Failed to load Aki predictions overview')
      })
    } finally {
      isAkiLoading.value = false
    }
  }

  async function getEdsScoresForAkiPatients() {
    if (!totalAkiPatients.value.length) return

    try {
      const batchScoreRequestPayload: BatchScoreRequestItemDto[] = totalAkiPatients.value.map(
        (patientData) => ({
          caseId: patientData.case.id,
          wardId: patientData.case.ward.id
        })
      )
      const edsScores = await EdsService.getClinicalScoresBatch(batchScoreRequestPayload)
      totalAkiPatients.value = totalAkiPatients.value.map((patientData) => ({
        ...patientData,
        edsScore: edsScores.find((score) => score.caseId === patientData.case.id)
      }))
    } catch {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Failed to load EDS scores'
      })
    }
  }

  // TODO(TSDC-2178 follow-up): getAkiOverview is already slow (~15s+) and this fires N
  // parallel per-case requests on top of it. Move to a batch endpoint (like
  // EdsService.getClinicalScoresBatch) once the backend exposes organ support + temperature
  // on the AKI overview response, and remove this per-case fallback.
  async function getOrganSupportAndVitalSignsForAkiPatients() {
    if (!totalAkiPatients.value.length) return

    try {
      isAkiCardDetailsLoading.value = true

      const details = await Promise.all(
        totalAkiPatients.value.map(async (patientData) => {
          const [organSupport, vitalSignsResponse] = await Promise.all([
            OrganSupportService.getOrganSupport(patientData.case.id, undefined),
            VitalSignsService.getLatestVitalSigns(patientData.case.id, undefined)
          ])

          return {
            caseId: patientData.case.id,
            organSupport,
            vitalSigns: formatVitalSigns(
              (vitalSignsResponse.items ?? []).map((item) => item.vitalSigns[0])
            )
          }
        })
      )

      totalAkiPatients.value = totalAkiPatients.value.map((patientData) => {
        const detail = details.find((d) => d.caseId === patientData.case.id)
        return {
          ...patientData,
          organSupport: detail?.organSupport,
          vitalSigns: detail?.vitalSigns
        }
      })
    } catch {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Failed to load organ support and vital signs'
      })
    } finally {
      isAkiCardDetailsLoading.value = false
    }
  }

  const selectedInfoboxCaseId = ref<string | null>(null)
  const isInfoboxPanelLoading = ref(false)

  const selectedInfoboxPatient = computed<AkiCaseWithPredictionResult | null>(
    () => totalAkiPatients.value.find((p) => p.case.id === selectedInfoboxCaseId.value) ?? null
  )

  async function openInfobox(caseId: string) {
    selectedInfoboxCaseId.value = caseId
    const patient = totalAkiPatients.value.find((p) => p.case.id === caseId)
    if (patient && !patient.panelLoaded) {
      await fetchAkiInfoPanel(caseId)
    }
  }

  async function fetchAkiInfoPanel(caseId: string) {
    try {
      isInfoboxPanelLoading.value = true
      const panel = await AkiPredictionsService.getAkiInfoPanel(caseId)
      // Merge the fresh single-patient data into the existing list row without refetching the whole page.
      totalAkiPatients.value = totalAkiPatients.value.map((patientData) =>
        patientData.case.id === caseId
          ? {
              ...patientData,
              predictionResult: panel.predictionResult,
              usedInputs: {
                laboratory: panel.laboratory,
                vitalSigns: panel.vitalSigns,
                demography: panel.demography
              },
              organSupport: panel.organSupport,
              egfr: panel.eGFR,
              temperature: panel.temperature,
              vasopressors: panel.vasopressors,
              trend: panel.trend ?? undefined,
              panelLoaded: true
            }
          : patientData
      )
    } catch {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: i18n.global.t('Failed to load AKI detail panel')
      })
    } finally {
      isInfoboxPanelLoading.value = false
    }
  }

  function closeInfobox() {
    selectedInfoboxCaseId.value = null
  }

  function setUdiData(data: PluginUdiDto | null) {
    udiData.value = data
  }

  return {
    getAkiPredictionsForUser,
    getOrganSupportAndVitalSignsForAkiPatients,
    setUdiData,
    currentPage,
    itemsPerPage,
    sortBy,
    sortDirection,
    totalAkiPatientsCount,
    veryHighRiskAkiPatientsCount,
    highRiskAkiPatientsCount,
    isAkiLoading,
    isAkiCardDetailsLoading,
    totalAkiPatients,
    udiData,
    selectedInfoboxCaseId,
    selectedInfoboxPatient,
    isInfoboxPanelLoading,
    openInfobox,
    closeInfobox
  }
})
